import React, { useState } from 'react';
import {
        Box,
        Card,
        Typography,
        Button,
        TextField,
        InputAdornment,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        Chip,
        Grid,
        FormControl,
        InputLabel,
        Select,
        MenuItem,
        useTheme,
} from '@mui/material';
import {
        Search,
        Add,
        AssignmentReturn,
        CheckCircle,
        Pending,
        Cancel,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, InventoryTransaction } from '../../services/simpleApi';

interface Return {
	id: string;
	order_id: string;
	type: 'customer' | 'supplier';
	status: 'pending' | 'approved' | 'rejected' | 'completed';
	created_at: string;
	items: any[];
}

const ReturnsPage: React.FC = () => {
	const theme = useTheme();
	const [searchQuery, setSearchQuery] = useState('');
	const [returns, setReturns] = useState<Return[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const fetchItems = async () => {
		try {
			setLoading(true);
			// Get return transactions
			const response = await simpleApi.inventory.getTransactions({ transaction_type: 'return' });
			const items: Return[] = response.items.map((t: InventoryTransaction) => ({
				id: t.id,
				order_id: t.reference_id || 'N/A',
				type: t.reference_type === 'supplier_return' ? 'supplier' : 'customer',
				status: 'completed',
				created_at: t.created_at,
				items: [{ product_id: t.product_id, quantity: t.quantity }],
			}));
			setReturns(items);
			setError(null);
		} catch (err: any) {
			setError(err.message || 'Failed to load returns');
		} finally {
			setLoading(false);
		}
	};

	React.useEffect(() => {
		fetchItems();
	}, []);

	const clearSuccess = () => setSuccess(null);        const filteredReturns = (returns || []).filter(ret =>
                ret.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ret.order_id.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const getStatusConfig = (status: string) => {
                switch (status) {
                        case 'completed':
                                return { color: 'success', icon: <CheckCircle />, label: 'Completed' };
                        case 'approved':
                                return { color: 'info', icon: <CheckCircle />, label: 'Approved' };
                        case 'pending':
                                return { color: 'warning', icon: <Pending />, label: 'Pending' };
                        case 'rejected':
                                return { color: 'error', icon: <Cancel />, label: 'Rejected' };
                        default:
                                return { color: 'default', icon: null, label: status };
                }
        };

        return (
                <Box>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                                Returns Management
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                                Handle customer RMAs and supplier returns
                                        </Typography>
                                </Box>
                                <Button variant="contained" startIcon={<Add />}>
                                        Create Return
                                </Button>
                        </Box>

                        {error && <ErrorAlert error={error} onRetry={fetchItems} />}

                        {/* Search and Filters */}
                        <Card sx={{ mb: 3, p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={6}>
                                                <TextField
                                                        fullWidth
                                                        placeholder="Search returns..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        InputProps={{
                                                                startAdornment: (
                                                                        <InputAdornment position="start">
                                                                                <Search />
                                                                        </InputAdornment>
                                                                ),
                                                        }}
                                                />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth>
                                                        <InputLabel>Type</InputLabel>
                                                        <Select label="Type" defaultValue="all">
                                                                <MenuItem value="all">All Types</MenuItem>
                                                                <MenuItem value="customer">Customer Return</MenuItem>
                                                                <MenuItem value="supplier">Supplier Return</MenuItem>
                                                        </Select>
                                                </FormControl>
                                        </Grid>
                                </Grid>
                        </Card>

                        {/* Returns Table */}
                        <Card>
                                {loading ? (
                                        <LoadingState message="Loading returns..." />
                                ) : filteredReturns.length === 0 ? (
                                        <EmptyState
                                                title="No returns found"
                                                description="Create a return request to get started"
                                                actionLabel="Create Return"
                                                icon={<AssignmentReturn sx={{ fontSize: 64, color: 'text.disabled' }} />}
                                        />
                                ) : (
                                        <TableContainer>
                                                <Table>
                                                        <TableHead>
                                                                <TableRow>
                                                                        <TableCell>Return ID</TableCell>
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>Order ID</TableCell>
                                                                        <TableCell>Type</TableCell>
                                                                        <TableCell>Status</TableCell>
                                                                        <TableCell align="right">Actions</TableCell>
                                                                </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                                {filteredReturns.map((ret) => {
                                                                        const statusConfig = getStatusConfig(ret.status);

                                                                        return (
                                                                                <TableRow key={ret.id} hover>
                                                                                        <TableCell>
                                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                        {ret.id.substring(0, 8)}...
                                                                                                </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                                {new Date(ret.created_at).toLocaleDateString('en-GB')}
                                                                                        </TableCell>
                                                                                        <TableCell>{ret.order_id}</TableCell>
                                                                                        <TableCell>
                                                                                                <Chip
                                                                                                        label={ret.type === 'customer' ? 'Customer' : 'Supplier'}
                                                                                                        size="small"
                                                                                                        variant="outlined"
                                                                                                />
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                                <Chip
                                                                                                        icon={statusConfig.icon || undefined}
                                                                                                        label={statusConfig.label}
                                                                                                        size="small"
                                                                                                        color={statusConfig.color as any}
                                                                                                />
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                                <Button size="small">Process</Button>
                                                                                        </TableCell>
                                                                                </TableRow>
                                                                        );
                                                                })}
                                                        </TableBody>
                                                </Table>
                                        </TableContainer>
                                )}
                        </Card>

                        <SuccessSnackbar
                                open={!!success}
                                message={success || ''}
                                onClose={clearSuccess}
                        />
                </Box>
        );
};

export default ReturnsPage;
